import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { MeetingHolidayApiService } from '../../../services/meeting-holiday-api.service';
import { MeetingHolidayDrawerService } from '../../../services/meeting-holiday-drawer.service';
import { MeetingHolidayResponse } from '../../../models/meeting.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { CurrencyService } from '../../../../currency/currency.service';
import { CountryResponse } from '../../../../currency/models/currency.model';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-holidays-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './holidays-list.component.html',
})
export class HolidaysListComponent implements OnInit, OnDestroy {
  items: MeetingHolidayResponse[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';
  filtroRecurring: '' | 'true' | 'false' = '';
  filtroCountry = ''; // código ISO; se filtra en el servidor (INC 28)
  countries: CountryResponse[] = [];

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: MeetingHolidayApiService,
    private drawerService: MeetingHolidayDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private currencyService: CurrencyService
  ) {}

  ngOnInit(): void {
    const savedPageSize = localStorage.getItem('holidays:pageSize');
    if (savedPageSize) this.pageSize = +savedPageSize;
    this.currencyService.getCountries().subscribe({
      next: (countries) =>
        (this.countries = [...countries].sort((a, b) => a.name.localeCompare(b.name))),
      error: () => console.error('Error cargando países'),
    });
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.list(this.businessId, this.globalPage, this.pageSize,
        this.filtroCountry || undefined).subscribe({
      next: (page) => {
        let data = [...page.content];
        if (this.filtroSearch.trim()) {
          const term = this.filtroSearch.trim().toLowerCase();
          data = data.filter(
            (i) =>
              i.name.toLowerCase().includes(term) ||
              (i.countryCode || '').toLowerCase().includes(term)
          );
        }
        if (this.filtroActive !== '') {
          const want = this.filtroActive === 'true';
          data = data.filter((i) => !!i.isActive === want);
        }
        if (this.filtroRecurring !== '') {
          const want = this.filtroRecurring === 'true';
          data = data.filter((i) => !!i.isRecurring === want);
        }
        this.items = data.sort((a, b) =>
          (a.holidayDate || '').localeCompare(b.holidayDate || '')
        );
        this.globalTotalPages = page.totalPages;
        this.globalTotalElements = page.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando feriados';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('holidays:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: MeetingHolidayResponse): void {
    this.drawerService.open({ holidayId: item.id });
  }

  toggle(item: MeetingHolidayResponse): void {
    this.api
      .update(item.id, this.businessId, {
        businessId: this.businessId,
        holidayDate: item.holidayDate,
        name: item.name,
        countryCode: item.countryCode,
        isRecurring: item.isRecurring,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: MeetingHolidayResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar feriado',
      message: `¿Eliminar "${item.name}"?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Feriado', `Se eliminó "${item.name}" correctamente.`); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  countryCodeOf(c: CountryResponse): string {
    return c.alphabeticCode || c.code || '';
  }

  trackById = (_: number, item: MeetingHolidayResponse) => item.id;
}
